import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import GlobalSearchBar from "@/components/feature/GlobalSearchBar";
import { useSearchResults } from "@/hooks/useSearch";
import { categories, sectors } from "@/mocks/articles";
import { useSeoMeta } from "@/hooks/useSeoMeta";
import { useJsonLd } from "@/hooks/useJsonLd";

const categoryColors: Record<string, string> = {
  "Success Story": "bg-orange-500",
  "Startup": "bg-green-600",
  "Innovation": "bg-yellow-500",
  "Femme Entrepreneuse": "bg-pink-500",
  "Jeune Talent": "bg-purple-500",
  "Diaspora Africaine": "bg-gray-700",
};

function highlightText(text: string, query: string): React.ReactNode {
  if (!query.trim()) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-orange-100 text-orange-700 rounded px-0.5 not-italic">{text.slice(idx, idx + query.length)}</mark>
      {text.slice(idx + query.length)}
    </>
  );
}

export default function RecherchePage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get("q") ?? "";
  const initialCategory = searchParams.get("category") ?? t("search.allCategories");
  const siteUrl = import.meta.env.VITE_SITE_URL || "https://example.com";

  const [category, setCategory] = useState(initialCategory);
  const [sector, setSector] = useState(sectors[0] ?? "");
  const [sortBy, setSortBy] = useState<"relevance" | "date" | "views">("relevance");
  const [filtersOpen, setFiltersOpen] = useState(false);

  const { results, loading, total } = useSearchResults(initialQuery, category, sector);

  useEffect(() => {
    setCategory(searchParams.get("category") ?? t("search.allCategories"));
  }, [searchParams, t]);

  const handleCategoryChange = (cat: string) => {
    setCategory(cat);
    const params = new URLSearchParams(searchParams);
    const allCat = t("search.allCategories");
    if (cat !== allCat) params.set("category", cat);
    else params.delete("category");
    setSearchParams(params);
  };

  const sortedResults = [...results].sort((a, b) => {
    if (sortBy === "date") return new Date(b.date).getTime() - new Date(a.date).getTime();
    if (sortBy === "views") return (b.views ?? 0) - (a.views ?? 0);
    return 0;
  });

  const popularSearches = ["Fintech", "Femmes", "Diaspora", "Kenya", "Nigeria", "Startup", "Innovation", "Sénégal"];
  const activeFiltersCount = [category !== t("search.allCategories"), sector !== sectors[0]].filter(Boolean).length;

  useSeoMeta({
    title: initialQuery
      ? `Recherche "${initialQuery}" — Wazi Stories Afrique`
      : "Recherche — Wazi Stories Afrique",
    description: "Recherchez parmi des centaines de success stories, startups, innovations et portraits d'entrepreneurs africains sur Wazi Stories Afrique.",
    type: "website",
    keywords: "recherche entrepreneur africain, Wazi Stories recherche, startup Afrique, innovation africaine",
  });

  useJsonLd({
    "@context": "https://schema.org",
    "@type": "SearchResultsPage",
    "name": initialQuery
      ? `Résultats pour "${initialQuery}" — Wazi Stories Afrique`
      : "Recherche — Wazi Stories Afrique",
    "description": "Recherchez parmi des centaines de success stories, startups et innovations d'entrepreneurs africains.",
    "url": `${siteUrl}/recherche${initialQuery ? `?q=${encodeURIComponent(initialQuery)}` : ""}`,
    "isPartOf": {
      "@type": "WebSite",
      "name": "Wazi Stories Afrique",
      "url": siteUrl,
      "potentialAction": {
        "@type": "SearchAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": `${siteUrl}/recherche?q={search_term_string}`
        },
        "query-input": "required name=search_term_string"
      }
    }
  }, "recherche");

  return (
    <div className="min-h-screen bg-[#fdf8f3]">
      <Navbar />

      {/* Hero search area */}
      <div className="pt-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 pb-12">
        <div className="max-w-3xl mx-auto px-4 md:px-8 pt-12 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-white font-serif mb-2">
            {t("recherche.title")}
          </h1>
          <p className="text-gray-400 text-sm mb-8">
            {total > 0 && initialQuery
              ? `${total} ${total > 1 ? t("recherche.subtitle.results_plural") : t("recherche.subtitle.results")} "${initialQuery}"`
              : t("recherche.subtitle.empty")}
          </p>
          <GlobalSearchBar
            autoFocus={!initialQuery}
            placeholder={t("nav.searchPlaceholder")}
            className="w-full"
          />

          {/* Popular searches */}
          {!initialQuery && (
            <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
              <span className="text-gray-500 text-xs">{t("recherche.trends")}</span>
              {popularSearches.map((term) => (
                <Link
                  key={term}
                  to={`/recherche?q=${encodeURIComponent(term)}`}
                  className="text-xs bg-white/10 hover:bg-orange-500 text-gray-300 hover:text-white px-3 py-1.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                >
                  {term}
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-6 md:py-10">

        {/* Mobile filter toggle */}
        <div className="flex items-center justify-between mb-4 lg:hidden">
          <p className="text-sm text-gray-600">
            {total > 0 && initialQuery ? (
              <><strong className="text-gray-900">{total}</strong> {total > 1 ? t("recherche.results.count_plural") : t("recherche.results.count")}</>
            ) : (
              <span className="text-gray-400">{t("recherche.filterHint")}</span>
            )}
          </p>
          <button
            onClick={() => setFiltersOpen((v) => !v)}
            className="flex items-center gap-2 text-sm font-semibold text-orange-500 border border-orange-200 px-4 py-2 rounded-full hover:bg-orange-50 transition-colors cursor-pointer"
          >
            <i className="ri-filter-3-line"></i>
            {t("recherche.filterToggle")}
            {activeFiltersCount > 0 && (
              <span className="w-5 h-5 flex items-center justify-center rounded-full bg-orange-500 text-white text-xs font-bold">
                {activeFiltersCount}
              </span>
            )}
          </button>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 md:gap-8">

          {/* ── Sidebar filters ── */}
          <aside className={`w-full lg:w-64 flex-shrink-0 ${filtersOpen ? "block" : "hidden"} lg:block`}>
            <div className="bg-white rounded-2xl border border-gray-100 p-5 lg:sticky lg:top-24">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                  <i className="ri-filter-3-line text-orange-500"></i>
                  {t("recherche.filters.title")}
                </h2>
                <button
                  onClick={() => setFiltersOpen(false)}
                  className="lg:hidden w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 cursor-pointer"
                >
                  <i className="ri-close-line text-sm"></i>
                </button>
              </div>

              {/* Category filter */}
              <div className="mb-5">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">{t("recherche.filters.category")}</p>
                <div className="flex flex-col gap-1">
                  {categories.map((cat) => (
                    <button
                      key={cat}
                      onClick={() => { handleCategoryChange(cat); setFiltersOpen(false); }}
                      className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-left transition-colors cursor-pointer ${
                        category === cat
                          ? "bg-orange-50 text-orange-600 font-semibold"
                          : "text-gray-600 hover:bg-gray-50"
                      }`}
                    >
                      {category === cat && <i className="ri-checkbox-circle-fill text-orange-500 text-xs"></i>}
                      {cat}
                    </button>
                  ))}
                </div>
              </div>

              {/* Sector filter */}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">{t("recherche.filters.sector")}</p>
                <select
                  value={sector}
                  onChange={(e) => setSector(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-orange-400 bg-white cursor-pointer"
                >
                  {sectors.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>

              {/* Reset */}
              {(category !== t("search.allCategories") || sector !== sectors[0]) && (
                <button
                  onClick={() => { setCategory(t("search.allCategories")); setSector(sectors[0] ?? ""); }}
                  className="mt-4 w-full text-xs text-gray-400 hover:text-red-500 transition-colors cursor-pointer flex items-center justify-center gap-1"
                >
                  <i className="ri-close-circle-line"></i>
                  {t("recherche.filters.reset")}
                </button>
              )}
            </div>
          </aside>

          {/* ── Results ── */}
          <div className="flex-1 min-w-0">
            {/* Sort bar */}
            {initialQuery && !loading && results.length > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <p className="text-sm text-gray-600">
                  <strong className="text-gray-900">{total}</strong> {total > 1 ? t("recherche.results.count_plural") : t("recherche.results.count")}
                  {initialQuery && <> {t("recherche.results.for")} <strong className="text-orange-500">&quot;{initialQuery}&quot;</strong></>}
                </p>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-gray-400">{t("recherche.sort.label")}</span>
                  {(["relevance", "date", "views"] as const).map((s) => (
                    <button
                      key={s}
                      onClick={() => setSortBy(s)}
                      className={`text-xs px-3 py-1.5 rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                        sortBy === s ? "bg-orange-500 text-white" : "bg-white border border-gray-200 text-gray-600 hover:border-orange-300"
                      }`}
                    >
                      {s === "relevance" ? t("recherche.sort.relevance") : s === "date" ? t("recherche.sort.date") : t("recherche.sort.views")}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Loading */}
            {loading && (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="bg-white rounded-2xl h-72 animate-pulse border border-gray-100"></div>
                ))}
              </div>
            )}

            {/* No query state */}
            {!loading && !initialQuery && (
              <div className="text-center py-16 md:py-20">
                <div className="w-20 h-20 flex items-center justify-center rounded-full bg-orange-50 mx-auto mb-4">
                  <i className="ri-search-line text-orange-400 text-4xl"></i>
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">{t("recherche.empty.title")}</h2>
                <p className="text-gray-500 text-sm max-w-sm mx-auto">
                  {t("recherche.empty.desc")}
                </p>
                <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 max-w-lg mx-auto">
                  {["Fintech Nigeria", "Femmes Kenya", "Diaspora France", "AgriTech Sénégal", "Innovation Ghana", "Startup Côte d'Ivoire"].map((term) => (
                    <Link
                      key={term}
                      to={`/recherche?q=${encodeURIComponent(term)}`}
                      className="bg-white border border-gray-100 rounded-xl px-3 md:px-4 py-3 text-sm text-gray-700 hover:border-orange-300 hover:text-orange-600 transition-colors cursor-pointer text-center"
                    >
                      {term}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* No results */}
            {!loading && initialQuery && results.length === 0 && (
              <div className="text-center py-16 md:py-20">
                <div className="w-20 h-20 flex items-center justify-center rounded-full bg-gray-100 mx-auto mb-4">
                  <i className="ri-search-line text-gray-400 text-4xl"></i>
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">{t("recherche.noResults.title")}</h2>
                <p className="text-gray-500 text-sm">
                  {t("recherche.noResults.desc")} &quot;{initialQuery}&quot;. {t("recherche.noResults.hint")}
                </p>
                <div className="mt-6 flex flex-wrap justify-center gap-2">
                  {popularSearches.map((term) => (
                    <Link
                      key={term}
                      to={`/recherche?q=${encodeURIComponent(term)}`}
                      className="text-sm bg-orange-50 text-orange-600 px-4 py-2 rounded-full hover:bg-orange-100 transition-colors cursor-pointer"
                    >
                      {term}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Results grid */}
            {!loading && sortedResults.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">
                {sortedResults.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-orange-200 hover:-translate-y-1 transition-all duration-300 cursor-pointer block"
                  >
                    <div className="relative h-40 md:h-44 overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                      <div className="absolute top-3 left-3">
                        <span className={`text-xs font-bold text-white px-3 py-1 rounded-full ${categoryColors[article.category] ?? "bg-gray-600"}`}>
                          {article.category}
                        </span>
                      </div>
                      {article.views && article.views > 10000 && (
                        <div className="absolute bottom-3 right-3 bg-black/40 backdrop-blur-sm rounded-full px-2 py-1 text-white text-xs flex items-center gap-1">
                          <i className="ri-fire-line text-orange-400"></i>
                          {(article.views / 1000).toFixed(0)}K {t("article.views")}
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <div className="flex flex-wrap items-center gap-2 text-xs text-gray-400 mb-2">
                        <span>{article.countryFlag} {article.country}</span>
                        <span>·</span>
                        <span>{article.sector}</span>
                        {article.readTime && <><span>·</span><span>{article.readTime} min</span></>}
                      </div>
                      <h3 className="font-bold text-gray-900 text-sm leading-snug line-clamp-2 group-hover:text-orange-500 transition-colors mb-2">
                        {highlightText(article.title, initialQuery)}
                      </h3>
                      <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed">
                        {highlightText(article.summary, initialQuery)}
                      </p>
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                        <div className="flex items-center gap-1.5">
                          <div className="w-5 h-5 rounded-full bg-orange-100 flex items-center justify-center">
                            <i className="ri-user-line text-orange-500 text-xs"></i>
                          </div>
                          <span className="text-xs text-gray-600 truncate max-w-[120px]">{article.entrepreneur}</span>
                        </div>
                        <span className="text-orange-500 text-xs font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                          {t("recherche.read")} <i className="ri-arrow-right-line"></i>
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
